// src/views/accounting/Vendors/useVendorList.js

import { ref, computed } from '@vue/composition-api'
import axios from '@axios'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { useListFilters } from '@/composables/useListFilters'

const { i18n } = useI18nUtils()
const i18nT = str => i18n(str)

export default function useVendorList() {
  // 1) Table refs & data
  const refDocumentListTable = ref(null)
  const vendors              = ref([])
  const totalDocuments       = ref(0)
  const perPageOptions       = [25, 50, 100, 200]
  const sortBy               = ref('name')
  const isSortDirDesc        = ref(false)

  // 2) Filter definitions
  const defs = [
    { param: 'page',     default: 1,  parse: raw => parseInt(raw, 10) || 1 },
    { param: 'perPage',  default: 50, parse: raw => parseInt(raw, 10) || 25 },
    { param: 'search',   default: '', parse: raw => raw },
    { param: 'type',     default: '', parse: raw => raw },
    { param: 'country',  default: '', parse: raw => raw },
  ]

  // 3) Wire up filters (URL-sync + table reload)
  const { filterRefs, filterParams } =
      useListFilters(defs, () => refetchData())

  // 4) Alias for component binding
  const {
    page:    currentPage,
    perPage,
    search:  searchQuery,
    type:    vendorType,
    country,
  } = filterRefs

  // 5) Table columns
  const tableColumns = [
    { key: 'checkbox',      label: '',                   thClass: 'tbl-chk',       tdClass: 'tbl-chk' },
    { key: 'name',          label: i18nT('Name'),        sortable: true,           thClass: 'pl-1', tdClass: 'pl-1' },
    { key: 'type',          label: i18nT('Type'),        sortable: true },
    { key: 'vat',           label: i18nT('VAT number'),  sortable: true,           thClass: 'sk-header-content' },
    { key: 'address',       label: i18nT('Address'),     sortable: true },
    { key: 'representative',label: i18nT('Representative'), sortable: true },
    { key: 'action',        label: i18nT('Actions'),     thClass: 'tbl-actions',   tdClass: 'sticky-column', stickyColumn: true },
  ]

  // 6) Data meta
  const dataMeta = computed(() => {
    const localCount = refDocumentListTable.value
        ? refDocumentListTable.value.localItems.length
        : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localCount ? 1 : 0),
      to:   perPage.value * (currentPage.value - 1) + localCount,
      of:   totalDocuments.value,
    }
  })

  // 7) Fetcher
  function fetchDocuments(ctx, callback) {
    const params = {
      page:          currentPage.value,
      show_per_page: perPage.value,
      sort_by:       ctx.sortBy || sortBy.value || 'name',
      sort_order:    ctx.sortDesc ? 'DESC' : 'ASC',
      search:        searchQuery.value,
    }

    Object.entries(filterParams.value).forEach(([key, val]) => {
      switch (key) {
        case 'type':
          if (val) params['filters[type]'] = val
          break
        case 'country':
          if (val) params['filters[country]'] = val
          break
        default:
          break
      }
    })

    axios
        .get('vendors', { params })
        .then(({ data }) => {
          const list = data.data.vendors || []
          vendors.value = list
          totalDocuments.value = data.data.pagination.records_count
          callback(list)
        })
  }

  // 8) Manual refetch for external calls
  function refetchData() {
    refDocumentListTable.value?.refresh()
  }

  return {
    // table
    refDocumentListTable,
    tableColumns,
    dataMeta,
    fetchDocuments,
    refetchData,

    // data
    vendors,
    totalDocuments,
    sortBy,
    isSortDirDesc,
    perPageOptions,

    // filters for v-model
    currentPage,
    perPage,
    searchQuery,
    vendorType,
    country,
  }
}

